import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Leaf, Search, Plus, ShoppingCart, Package, Truck, Filter,
  MapPin, Calendar, Sprout, ArrowLeft, Loader2, Store, Heart,
  Star, Printer, Wallet, Gift, Check, X, ChevronRight, Award, Mail
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// USVI Colors
const usviColors = {
  green: { primary: "#2D8B4E", light: "#A8D5BA", dark: "#1A5632" },
  gold: { primary: "#FFB81C", light: "#FFE4A0" },
  teal: { primary: "#00A6A0" }
};

// Challenge Seed Packs Component
const ChallengeSeedPacks = ({ user }) => {
  const [packs, setPacks] = useState([]);
  const [individualSeeds, setIndividualSeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPack, setSelectedPack] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [isVideTeacher, setIsVideTeacher] = useState(false);
  const [showVideModal, setShowVideModal] = useState(false);

  useEffect(() => {
    loadPacks();
  }, []);

  useEffect(() => {
    if (user?.email?.toLowerCase().endsWith("@vide.vi")) {
      setIsVideTeacher(true);
    }
  }, [user]);

  const loadPacks = async () => {
    try {
      const [packsRes, seedsRes] = await Promise.all([
        axios.get(`${API}/seed-packs`),
        axios.get(`${API}/individual-seeds`)
      ]);
      setPacks(packsRes.data.packs || []);
      setIndividualSeeds(seedsRes.data.seeds || []);
    } catch (error) {
      console.error("Failed to load seed packs:", error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (item, type) => {
    const existing = cart.find(c => c.id === item.id && c.type === type);
    if (existing) {
      setCart(cart.map(c => c.id === item.id && c.type === type ? {...c, quantity: c.quantity + 1} : c));
    } else {
      setCart([...cart, { ...item, type, quantity: 1 }]);
    }
    toast.success(`Added ${item.name} to cart!`);
  };

  const removeFromCart = (id) => {
    setCart(cart.filter(c => c.id !== id));
  };

  const calculateTotal = () => {
    if (isVideTeacher) return { subtotal: 0, discount: 0, total: 0, freeSeeds: 0 };
    
    let subtotal = 0;
    let seedCount = 0;
    
    cart.forEach(item => {
      if (item.type === "pack") {
        subtotal += 9 * item.quantity;
      } else {
        subtotal += 3 * item.quantity;
        seedCount += item.quantity;
      }
    });
    
    // Buy 4 get 5th free
    const freeSeeds = Math.floor(seedCount / 5);
    const discount = freeSeeds * 3;
    
    return { subtotal, discount, total: subtotal - discount, freeSeeds };
  };

  const placeOrder = async () => {
    if (!user) {
      toast.error("Please sign in to place an order");
      return;
    }

    try {
      const items = cart.map(item => ({
        type: item.type,
        id: item.type === "pack" ? item.pack_key : item.id,
        quantity: item.quantity
      }));

      const response = await axios.post(`${API}/seed-packs/order`, { 
        items,
        origin_url: window.location.origin 
      }, { withCredentials: true });
      
      // If checkout URL is returned, redirect to Stripe
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }
      
      // For VIDE teachers (free orders), show success
      toast.success(response.data.message);
      setCart([]);
      setShowCart(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to place order");
    }
  };

  const totals = calculateTotal();

  if (loading) {
    return (
      <div className="mb-12 p-8 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: usviColors.green.primary }} />
        <p className="text-gray-500 mt-2">Loading seed packs...</p>
      </div>
    );
  }

  return (
    <div className="mb-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: usviColors.green.dark }}>
            <Gift className="w-6 h-6" style={{ color: usviColors.gold.primary }} />
            Challenge Seed Packs
          </h2>
          <p className="text-gray-500">Curated seed collections for each monthly challenge • $9/pack • Free shipping</p>
        </div>
        <div className="flex gap-3">
          {!isVideTeacher && (
            <Button 
              variant="outline" 
              onClick={() => setShowVideModal(true)}
              className="border-2"
              style={{ borderColor: usviColors.teal.primary, color: usviColors.teal.primary }}
            >
              <Award className="w-4 h-4 mr-2" />
              USVI Teacher? Get FREE Packs
            </Button>
          )}
          <Button 
            onClick={() => setShowCart(true)} 
            className="relative"
            style={{ background: usviColors.green.primary }}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Cart ({cart.length})
            {cart.length > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full text-xs flex items-center justify-center"
                    style={{ background: usviColors.gold.primary, color: usviColors.green.dark }}>
                {cart.reduce((sum, item) => sum + item.quantity, 0)}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* VIDE Teacher Banner */}
      {isVideTeacher && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 rounded-xl flex items-center justify-between"
          style={{ background: `linear-gradient(135deg, ${usviColors.teal.primary}20, ${usviColors.gold.light})` }}
        >
          <div className="flex items-center gap-3">
            <Award className="w-8 h-8" style={{ color: usviColors.teal.primary }} />
            <div>
              <p className="font-bold" style={{ color: usviColors.green.dark }}>VIDE Teacher Verified!</p>
              <p className="text-sm text-gray-600">All seed packs are FREE for you. Thank you for educating the next generation!</p>
            </div>
          </div>
          <Badge style={{ background: usviColors.gold.primary, color: usviColors.green.dark }}>100% FREE</Badge>
        </motion.div>
      )}

      {/* Promo Banner */}
      <div className="mb-6 p-4 rounded-xl text-center" style={{ background: usviColors.gold.light }}>
        <p className="font-semibold" style={{ color: usviColors.green.dark }}>
          🌱 Buy 4 individual seeds, get the 5th FREE! • All packs include free shipping
        </p>
      </div>

      {/* Seed Packs Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        {packs.map((pack) => (
          <motion.div
            key={pack.id}
            whileHover={{ scale: 1.02 }}
            className="rounded-xl overflow-hidden cursor-pointer border-2 hover:shadow-lg transition-shadow"
            style={{ borderColor: usviColors.green.light }}
            onClick={() => setSelectedPack(pack)}
            data-testid={`seed-pack-${pack.pack_key}`}
          >
            <div className="p-4" style={{ background: `linear-gradient(180deg, ${usviColors.green.light}30, white)` }}>
              <div className="text-3xl mb-2">🌱</div>
              <h3 className="font-bold text-sm" style={{ color: usviColors.green.dark }}>{pack.name}</h3>
              <p className="text-xs text-gray-500 line-clamp-2 mt-1">{pack.description}</p>
              <div className="flex items-center justify-between mt-3">
                <span className="text-lg font-bold" style={{ color: usviColors.green.primary }}>
                  {isVideTeacher ? "FREE" : `$${pack.price}`}
                </span>
                <Badge variant="outline" className="text-xs">{pack.seed_count} seeds</Badge>
              </div>
              <Button 
                size="sm" 
                className="w-full mt-3"
                onClick={(e) => { e.stopPropagation(); addToCart(pack, "pack"); }}
                style={{ background: usviColors.green.primary }}
              >
                <Plus className="w-3 h-3 mr-1" /> Add Pack
              </Button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Individual Seeds Section */}
      <div className="mt-8">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: usviColors.green.dark }}>
          <Sprout className="w-5 h-5" style={{ color: usviColors.green.primary }} />
          Individual Seeds • $3 each
          <Badge style={{ background: usviColors.gold.primary, color: usviColors.green.dark }}>Buy 4, Get 5th FREE</Badge>
        </h3>
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {individualSeeds.slice(0, 12).map((seed) => (
            <motion.div
              key={seed.id}
              whileHover={{ scale: 1.05 }}
              className="p-3 rounded-lg border text-center cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => addToCart(seed, "seed")}
              data-testid={`individual-seed-${seed.id}`}
            >
              <div className="text-2xl mb-1">🌿</div>
              <p className="text-xs font-medium truncate" style={{ color: usviColors.green.dark }}>{seed.name}</p>
              <p className="text-xs text-gray-400">{seed.variety}</p>
              <p className="text-sm font-bold mt-1" style={{ color: usviColors.green.primary }}>
                {isVideTeacher ? "FREE" : "$3"}
              </p>
            </motion.div>
          ))}
        </div>
        {individualSeeds.length > 12 && (
          <Button variant="link" className="mt-4" style={{ color: usviColors.green.primary }}>
            View all {individualSeeds.length} seeds <ChevronRight className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Pack Detail Modal */}
      <AnimatePresence>
        {selectedPack && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPack(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl max-w-lg w-full p-6"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold" style={{ color: usviColors.green.dark }}>{selectedPack.name}</h3>
                <Button variant="ghost" size="sm" onClick={() => setSelectedPack(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <p className="text-gray-600 mb-4">{selectedPack.description}</p>
              <div className="space-y-2 mb-4">
                <h4 className="font-semibold" style={{ color: usviColors.green.primary }}>Included Seeds:</h4>
                {selectedPack.seeds.map((seed, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                    <div>
                      <span className="font-medium">{seed.name}</span>
                      <span className="text-sm text-gray-500 ml-2">({seed.variety})</span>
                    </div>
                    <Badge variant="outline">{seed.seeds_per_packet} seeds</Badge>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between pt-4 border-t">
                <div>
                  <span className="text-2xl font-bold" style={{ color: usviColors.green.primary }}>
                    {isVideTeacher ? "FREE" : `$${selectedPack.price}`}
                  </span>
                  <span className="text-sm text-gray-500 ml-2">+ Free Shipping</span>
                </div>
                <Button onClick={() => { addToCart(selectedPack, "pack"); setSelectedPack(null); }}
                        style={{ background: usviColors.green.primary }}>
                  <ShoppingCart className="w-4 h-4 mr-2" /> Add to Cart
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cart Modal */}
      <AnimatePresence>
        {showCart && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCart(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[80vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold" style={{ color: usviColors.green.dark }}>Your Cart</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowCart(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {cart.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">Your cart is empty</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3 mb-4">
                    {cart.map((item, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-gray-500">
                            {item.type === "pack" ? "Seed Pack" : `${item.variety}`} × {item.quantity}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-bold">
                            {isVideTeacher ? "FREE" : `$${(item.type === "pack" ? 9 : 3) * item.quantity}`}
                          </span>
                          <Button variant="ghost" size="sm" onClick={() => removeFromCart(item.id)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal</span>
                      <span>{isVideTeacher ? "FREE" : `$${totals.subtotal.toFixed(2)}`}</span>
                    </div>
                    {totals.freeSeeds > 0 && !isVideTeacher && (
                      <div className="flex justify-between text-sm" style={{ color: usviColors.green.primary }}>
                        <span>Buy 4 Get 5th Free ({totals.freeSeeds} free)</span>
                        <span>-${totals.discount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span>Shipping</span>
                      <span style={{ color: usviColors.green.primary }}>FREE</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold pt-2 border-t">
                      <span>Total</span>
                      <span style={{ color: usviColors.green.primary }}>
                        {isVideTeacher ? "FREE" : `$${totals.total.toFixed(2)}`}
                      </span>
                    </div>
                  </div>

                  {isVideTeacher && (
                    <div className="mt-4 p-3 rounded-lg text-sm" style={{ background: usviColors.teal.primary + "20" }}>
                      <Check className="w-4 h-4 inline mr-2" style={{ color: usviColors.teal.primary }} />
                      VIDE Teacher discount applied - 100% FREE!
                    </div>
                  )}

                  <Button 
                    className="w-full mt-4 h-12"
                    onClick={placeOrder}
                    disabled={!user}
                    style={{ background: usviColors.green.primary }}
                  >
                    {user ? (isVideTeacher ? "Get Free Seeds" : `Pay $${totals.total.toFixed(2)}`) : "Sign in to Order"}
                  </Button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* VIDE Verification Modal */}
      <AnimatePresence>
        {showVideModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowVideModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl max-w-md w-full p-6"
              onClick={e => e.stopPropagation()}
            >
              <div className="text-center">
                <Award className="w-16 h-16 mx-auto mb-4" style={{ color: usviColors.teal.primary }} />
                <h3 className="text-xl font-bold mb-2" style={{ color: usviColors.green.dark }}>USVI Teacher Program</h3>
                <p className="text-gray-600 mb-6">
                  VIDE (Virgin Islands Department of Education) teachers receive <strong>FREE seed packs</strong> to help educate students about sustainable agriculture!
                </p>
                <div className="p-4 rounded-lg mb-6" style={{ background: usviColors.gold.light }}>
                  <Mail className="w-6 h-6 mx-auto mb-2" style={{ color: usviColors.gold.primary }} />
                  <p className="text-sm font-medium">Sign in with your <strong>@vide.vi</strong> email to verify</p>
                </div>
                <div className="space-y-2 text-left mb-6">
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4" style={{ color: usviColors.green.primary }} />
                    <span>All challenge seed packs FREE</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4" style={{ color: usviColors.green.primary }} />
                    <span>Free shipping on all orders</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4" style={{ color: usviColors.green.primary }} />
                    <span>50 bonus seeds when you sign up for notifications</span>
                  </div>
                </div>
                <Button onClick={() => setShowVideModal(false)} className="w-full" style={{ background: usviColors.green.primary }}>
                  Got it!
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MarketplacePage = () => {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    organic: null,
    heirloom: null
  });
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadListings();
    if (user) loadFavorites();
  }, [filters, user]);

  const loadListings = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (filters.organic !== null) params.append("organic", filters.organic);
      if (filters.heirloom !== null) params.append("heirloom", filters.heirloom);
      
      const response = await axios.get(`${API}/marketplace/listings?${params}`);
      setListings(response.data);
    } catch (error) {
      console.error("Failed to load listings:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/favorites`, { withCredentials: true });
      setFavorites(response.data.map(f => f.id));
    } catch (error) {
      console.error("Failed to load favorites:", error);
    }
  };

  const toggleFavorite = async (listingId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error("Please sign in to save favorites");
      return;
    }

    try {
      if (favorites.includes(listingId)) {
        await axios.delete(`${API}/marketplace/favorites/${listingId}`, { withCredentials: true });
        setFavorites(f => f.filter(id => id !== listingId));
        toast.success("Removed from favorites");
      } else {
        await axios.post(`${API}/marketplace/favorites/${listingId}`, {}, { withCredentials: true });
        setFavorites(f => [...f, listingId]);
        toast.success("Added to favorites");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update favorites");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadListings();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-serif text-xl font-bold text-foreground flex items-center gap-2">
                <Store className="w-5 h-5 text-secondary" />
                Seed Marketplace
              </h1>
              <p className="text-sm text-muted-foreground">Buy & sell heirloom seeds</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user && (
              <>
                <Link to="/marketplace/wallet">
                  <Button variant="outline" size="sm">
                    <Wallet className="w-4 h-4 mr-2" />
                    Wallet
                  </Button>
                </Link>
                <Link to="/marketplace/favorites">
                  <Button variant="outline" size="sm">
                    <Heart className="w-4 h-4 mr-2" />
                    Favorites
                  </Button>
                </Link>
                <Link to="/marketplace/my-orders">
                  <Button variant="outline" size="sm">
                    <Package className="w-4 h-4 mr-2" />
                    My Orders
                  </Button>
                </Link>
                <Link to="/marketplace/seed-packet">
                  <Button variant="outline" size="sm">
                    <Printer className="w-4 h-4 mr-2" />
                    Packet Designer
                  </Button>
                </Link>
                <Link to="/marketplace/sell">
                  <Button className="btn-primary" size="sm" data-testid="sell-seeds-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Sell Seeds
                  </Button>
                </Link>
              </>
            )}
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="RootSpace.Pro" className="h-8 w-auto" />
              
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Challenge Seed Packs Section */}
        <ChallengeSeedPacks user={user} />

        {/* Search & Filters */}
        <div className="mb-8">
          <form onSubmit={handleSearch} className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search seeds (tomatoes, peppers, herbs...)"
                className="pl-10"
                data-testid="search-seeds"
              />
            </div>
            <Button type="submit" data-testid="search-btn">Search</Button>
          </form>
          
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={filters.organic === true ? "default" : "outline"}
              size="sm"
              onClick={() => setFilters(f => ({ ...f, organic: f.organic === true ? null : true }))}
            >
              <Sprout className="w-4 h-4 mr-1" />
              Organic
            </Button>
            <Button
              variant={filters.heirloom === true ? "default" : "outline"}
              size="sm"
              onClick={() => setFilters(f => ({ ...f, heirloom: f.heirloom === true ? null : true }))}
            >
              <Calendar className="w-4 h-4 mr-1" />
              Heirloom
            </Button>
          </div>
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h2 className="text-xl font-semibold mb-2">No seeds listed yet</h2>
            <p className="text-muted-foreground mb-6">Be the first to list your seeds!</p>
            {user && (
              <Link to="/marketplace/sell">
                <Button className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  List Your Seeds
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((listing) => (
              <motion.div
                key={listing.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Link to={`/marketplace/listing/${listing.id}`}>
                  <Card className="card-organic hover:border-primary/50 transition-all cursor-pointer h-full relative">
                    {/* Favorite Button */}
                    <button
                      onClick={(e) => toggleFavorite(listing.id, e)}
                      className="absolute top-4 right-4 z-10 p-2 bg-white/80 rounded-full hover:bg-white transition-colors"
                      data-testid={`favorite-${listing.id}`}
                    >
                      <Heart 
                        className={`w-5 h-5 ${favorites.includes(listing.id) ? "fill-red-500 text-red-500" : "text-gray-400"}`} 
                      />
                    </button>

                    <CardContent className="pt-6">
                      {/* Badges */}
                      <div className="flex gap-2 mb-3">
                        {listing.is_organic && (
                          <Badge variant="secondary" className="bg-green-100 text-green-700">
                            <Sprout className="w-3 h-3 mr-1" />
                            Organic
                          </Badge>
                        )}
                        {listing.is_heirloom && (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                            Heirloom
                          </Badge>
                        )}
                      </div>

                      {/* Title & Variety */}
                      <h3 className="font-serif text-lg font-bold text-foreground mb-1">
                        {listing.plant_name}
                      </h3>
                      {listing.variety && (
                        <p className="text-sm text-muted-foreground mb-2">{listing.variety}</p>
                      )}

                      {/* Description */}
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {listing.description}
                      </p>

                      {/* Info Row */}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {listing.origin_location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {listing.year_harvested}
                        </span>
                        {listing.average_rating > 0 && (
                          <span className="flex items-center gap-1">
                            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                            {listing.average_rating} ({listing.review_count})
                          </span>
                        )}
                      </div>

                      {/* Price & Seller */}
                      <div className="flex items-center justify-between pt-3 border-t border-border">
                        <div>
                          <span className="text-2xl font-bold text-primary">
                            ${listing.price.toFixed(2)}
                          </span>
                          <span className="text-xs text-muted-foreground ml-1">
                            / {listing.seed_count_per_packet} seeds
                          </span>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Sold by</p>
                          <p className="text-sm font-medium">{listing.seller_name}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {/* How It Works Section */}
        <div className="mt-16 p-8 bg-muted/30 rounded-2xl">
          <h2 className="font-serif text-2xl font-bold text-center mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Browse & Buy</h3>
              <p className="text-sm text-muted-foreground">
                Search heirloom and organic seeds from fellow gardeners
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="font-semibold mb-2">Secure Shipping</h3>
              <p className="text-sm text-muted-foreground">
                Payment held until seller ships with tracking
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sprout className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold mb-2">Grow Together</h3>
              <p className="text-sm text-muted-foreground">
                Support seed diversity and community gardeners
              </p>
            </div>
          </div>
          <p className="text-center text-sm text-muted-foreground mt-6">
            ROOTSPACE takes an 18% commission to maintain the platform. Sellers receive payment once shipping is confirmed.
          </p>
        </div>
      </main>
    </div>
  );
};

export default MarketplacePage;
